import React from 'react'
import {
	Box, Card, CardHeader, Typography, Table, TableHead, TableBody, TableRow, TableCell,
	Divider, IconButton, Grid
} from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'


const DEMO_NOTIFICATIONS = [
	{
		condition: { name: 'Spain Cheaper Alert', comparator: 'Cheaper', value: 15, unit: '%' },
		pickupDate: '15 Mar 2025',
		lor: 5,
		category: { main: 'Economy', comp: 'Economy' },
		acriss: { main: 'ECMR', comp: 'ECMR' },
		fuelPolicy: { main: 'Full/Full', comp: 'Full/Full' },
		transmission: { main: 'Manual', comp: 'Manual' },
		mileage: { main: 'Unlimited', comp: 'Unlimited' },
		carDescription: { main: 'Fiat 500 or similar', comp: 'Toyota Yaris or similar' },
		site: { main: 'rentalspot.demo', comp: 'pricefly.demo' },
		provider: { main: 'AutoRent', comp: 'AutoRent' },
		price: { main: '92.30', comp: '78.45' },
		diff: -15.0,
	},
	{
		condition: { name: 'UK Price Difference', comparator: 'Cheaper/More expensive', value: 20, unit: '$' },
		pickupDate: '20 Mar 2025',
		lor: 7,
		category: { main: 'Compact', comp: 'Compact' },
		acriss: { main: 'CDMR', comp: 'CDMR' },
		fuelPolicy: { main: 'Full/Full', comp: 'Pick up Full/Return Full' },
		transmission: { main: 'Automatic', comp: 'Automatic' },
		mileage: { main: 'Unlimited', comp: '1500 km' },
		carDescription: { main: 'VW Golf or similar', comp: 'Ford Focus or similar' },
		site: { main: 'eurodrive.demo', comp: 'rentalspot.demo' },
		provider: { main: 'SpeedCar', comp: 'SpeedCar' },
		price: { main: '145.80', comp: '122.60' },
		diff: 18.9,
	},
	{
		condition: { name: 'France More Expensive', comparator: 'More expensive', value: 25, unit: '%' },
		pickupDate: '10 Apr 2025',
		lor: 3,
		category: { main: 'SUV', comp: 'SUV' },
		acriss: { main: 'SFMR', comp: 'SFMR' },
		fuelPolicy: { main: 'Full/Full', comp: 'Full/Full' },
		transmission: { main: 'Automatic', comp: 'Manual' },
		mileage: { main: 'Unlimited', comp: 'Unlimited' },
		carDescription: { main: 'Peugeot 3008 or similar', comp: 'Nissan Qashqai or similar' },
		site: { main: 'pricefly.demo', comp: 'bookride.demo' },
		provider: { main: 'DrivePro', comp: 'DrivePro' },
		price: { main: '210.50', comp: '168.40' },
		diff: 25.0,
	},
	{
		condition: { name: 'Germany Economy', comparator: 'Cheaper', value: 10, unit: '$' },
		pickupDate: '05 Apr 2025',
		lor: 14,
		category: { main: 'Intermediate', comp: 'Intermediate' },
		acriss: { main: 'IDMR', comp: 'IDMR' },
		fuelPolicy: { main: 'Full/Full', comp: 'Full/Full' },
		transmission: { main: 'Manual', comp: 'Manual' },
		mileage: { main: '2000 km', comp: 'Unlimited' },
		carDescription: { main: 'Opel Astra or similar', comp: 'Skoda Octavia or similar' },
		site: { main: 'bookride.demo', comp: 'autolink.demo' },
		provider: { main: 'Velocar', comp: 'Velocar' },
		price: { main: '385.20', comp: '362.10' },
		diff: -6.0,
	},
]

const LEGEND = [
	{ icon: 'percent', label: 'Percentage' },
	{ icon: 'money-bills', label: 'Currency', color: '#2e7d32' },
	{ icon: 'chart-line-down', label: 'Cheaper', color: '#d32f2f' },
	{ icon: 'chart-line-up', label: 'More Expensive', color: '#2e7d32' },
	{ icon: 'chart-line-up-down', label: 'Price Difference', color: '#ed6c02' },
]

const ICON_MAP = {
	'More expensive': { icon: 'chart-line-up', color: '#2e7d32' },
	'Cheaper': { icon: 'chart-line-down', color: '#d32f2f' },
	'Cheaper/More expensive': { icon: 'chart-line-up-down', color: '#ed6c02' },
	'$': { icon: 'money-bills', color: '#2e7d32' },
	'%': { icon: 'percent', color: '#000' },
}

function ComparatorCell({ main, comp }) {
	return <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
		<Typography sx={{ fontWeight: 'bold', textAlign: 'center', fontSize: '0.75rem' }}>{main}</Typography>
		{comp && <>
			<Divider sx={{ my: 0.8, width: '80%', borderBottom: '2px solid' }} />
			<Typography sx={{ textAlign: 'center', fontSize: '0.75rem' }}>{comp}</Typography>
		</>}
	</Box>
}

function SiteCell({ main, comp }) {
	return <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
		<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
			<Typography sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>{main}</Typography>
		</Box>
		{comp && <>
			<Divider sx={{ my: 0.8, width: '80%', borderBottom: '2px solid' }} />
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
				<Typography sx={{ fontSize: '0.75rem' }}>{comp}</Typography>
			</Box>
		</>}
	</Box>
}


export default function NotificationsDemoData() {
	return <Grid container spacing={2}>
		<Grid item xs={12}>
			<Card>
				<CardHeader title={
					<Typography variant="subtitle1" fontWeight={600}>Notifications: Price Comparison</Typography>
				} />

				<Box sx={{ display: 'flex', justifyContent: 'center', pb: 2, gap: 3 }}>
					{LEGEND.map((l, idx) => (
						<Box key={idx} sx={{
							display: 'flex', flexDirection: 'column', alignItems: 'center',
							cursor: 'pointer', mx: 1,
							transition: 'transform 0.3s ease-in-out',
							'&:hover': { transform: 'scale(1.05)' }
						}}>
							<IconSeeker icon={l.icon} sx={{ mt: 0.5, mb: 0, fontSize: 16, color: l.color || 'text.primary' }} />
							<Typography sx={{ mt: 0, fontWeight: 'bold' }}>{l.label}</Typography>
						</Box>
					))}
				</Box>

				<Box sx={{ px: 2, pb: 2, overflowX: 'auto' }}>
					<Table size="small">
						<TableHead>
							<TableRow sx={{ '& th': { bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.7rem', whiteSpace: 'nowrap', textAlign: 'center' } }}>
								<TableCell>Condition</TableCell>
								<TableCell>Pickup Date</TableCell>
								<TableCell>LOR</TableCell>
								<TableCell>Category</TableCell>
								<TableCell>Acriss</TableCell>
								<TableCell>Fuel Policy</TableCell>
								<TableCell>Transmission</TableCell>
								<TableCell>Mileage</TableCell>
								<TableCell>Car Description</TableCell>
								<TableCell>Site</TableCell>
								<TableCell>Provider</TableCell>
								<TableCell>Price</TableCell>
								<TableCell>Diff</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{DEMO_NOTIFICATIONS.map((n, idx) => (
								<TableRow key={idx} sx={{ '& td': { py: 1.5 } }}>
									<TableCell>
										<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
											<Typography sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{n.condition.name}</Typography>
											<Divider sx={{ my: 0.8, width: '80%', borderBottom: '2px solid' }} />
											<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
												<Typography sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{n.condition.value}</Typography>
												<IconSeeker
													icon={ICON_MAP[n.condition.unit]?.icon}
													sx={{ fontSize: 14, color: ICON_MAP[n.condition.unit]?.color, mx: 0.3 }}
												/>
												<IconSeeker
													icon={ICON_MAP[n.condition.comparator]?.icon}
													sx={{ fontSize: 14, color: ICON_MAP[n.condition.comparator]?.color }}
												/>
											</Box>
										</Box>
									</TableCell>

									<TableCell>
										<Typography sx={{ textAlign: 'center', fontSize: '0.8rem' }}>{n.pickupDate}</Typography>
									</TableCell>

									<TableCell>
										<Typography sx={{ textAlign: 'center', fontSize: '0.8rem' }}>{n.lor}</Typography>
									</TableCell>

									<TableCell><ComparatorCell main={n.category.main} comp={n.category.comp} /></TableCell>
									<TableCell><ComparatorCell main={n.acriss.main} comp={n.acriss.comp} /></TableCell>
									<TableCell><ComparatorCell main={n.fuelPolicy.main} comp={n.fuelPolicy.comp} /></TableCell>
									<TableCell><ComparatorCell main={n.transmission.main} comp={n.transmission.comp} /></TableCell>
									<TableCell><ComparatorCell main={n.mileage.main} comp={n.mileage.comp} /></TableCell>
									<TableCell><ComparatorCell main={n.carDescription.main} comp={n.carDescription.comp} /></TableCell>
									<TableCell><SiteCell main={n.site.main} comp={n.site.comp} /></TableCell>
									<TableCell><ComparatorCell main={n.provider.main} comp={n.provider.comp} /></TableCell>

									<TableCell>
										<ComparatorCell main={`€${n.price.main}`} comp={`€${n.price.comp}`} />
									</TableCell>

									<TableCell>
										<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
											<Typography sx={{
												textAlign: 'center', fontSize: '0.8rem',
												color: n.diff < 0 ? '#2e7d32' : '#d32f2f',
												fontWeight: 600,
											}}>
												{n.diff > 0 ? '+' : ''}{n.diff}%
											</Typography>
											<Divider sx={{ my: 0.8, width: '80%', borderBottom: '2px solid', borderColor: 'secondary.lighter' }} />
											<IconButton size="small" title="View in real time searches" sx={{ color: 'primary.main', fontSize: '1.4rem' }}>
												<IconSeeker icon="magnifying-glass-chart" />
											</IconButton>
										</Box>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			</Card>
		</Grid>
	</Grid>
}
